// Hashtag.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import MessageCard from '../container/MessageCard.js';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import {
  textColor,
  color,
  headerHeight,
  footerNavigationHeight,
} from '../style/variables.js';
import BackIcon from '../../img/ic_topbar_back.svg';
import LoadingImage from '../../img/loading.png';
import Spinning from '../style/Spinning.js';
import media from '../style/media.js';
import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import { useInView } from 'react-intersection-observer';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import withRouter from '../component/WithRouter.jsx';
import FlixListItem from '../container/FlixListItem.js';
import SortButton from '../container/SortButton.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getToObject from '../resource/getToObject.js';
import i18n from '../resource/i18n.js';

const OBSERVER_OPTIONS = {
  threshold: 0.2,
  triggerOnce: true,
};

const isServer = typeof window === 'undefined';
const hashtagTypes = ['story', 'flix'];

export class Hashtag extends React.PureComponent {
  constructor(props) {
    super(props);

    const { type, hashtag, replace } = this.props;
    const isTypeValid = hashtagTypes.includes(type);
    if (!isTypeValid) {
      replace(encodeURI(`/hashtag/${hashtag}/flix`));
    }

    const { staticContext } = this.props;
    // server side fetching
    if (staticContext.actions && hashtag && isTypeValid) {
      const { fetchStatus } = this.props;
      if ('error' === fetchStatus.status) {
        staticContext.error = fetchStatus.error;
      }

      const shouldFetchSearch = !['fetching', 'fetched'].includes(
        fetchStatus.status
      );
      if (shouldFetchSearch) {
        const { feedNameWithQuery, fetchFeeds } = this.props;
        staticContext.actions.push({
          function: fetchFeeds,
          argumentObject: {
            type: feedNameWithQuery,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }

      const { messageIds, isFirstMessageDetailFetched } = this.props;
      const shouldFetchDetails =
        messageIds.length && !isFirstMessageDetailFetched;
      if (shouldFetchDetails) {
        const { fetchMessageDetail } = this.props;
        messageIds.forEach(messageId => {
          staticContext.actions.push({
            function: fetchMessageDetail,
            argumentObject: {
              id: messageId,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        });
      }
    }
  }

  componentDidMount() {
    const {
      hashtag,
      feedNameWithQuery,
      fetchFeeds,
      fetchHashtagSearch,
      type,
      replace,
    } = this.props;
    if (!hashtagTypes.includes(type)) {
      return replace(encodeURI(`/hashtag/${hashtag}/flix`));
    }
    fetchHashtagSearch({ query: `#${hashtag}` });

    const { fetchStatus } = this.props;
    const shouldFetchSearch = !['fetching', 'fetched'].includes(
      fetchStatus.status
    );
    if (shouldFetchSearch) {
      fetchFeeds({
        type: feedNameWithQuery,
      });
    }
  }
  componentDidUpdate(prevProps) {
    const { isAuthed, feedNameWithQuery, fetchFeeds } = this.props;
    if (
      isAuthed !== prevProps.isAuthed ||
      feedNameWithQuery !== prevProps.feedNameWithQuery
    ) {
      fetchFeeds({ type: feedNameWithQuery });
    }
  }
  componentWillUnmount() {
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'Hashtag';
    }
  }

  getTabHref = tab => {
    const { location } = this.props;

    const { pathname, search } = getToObject({
      to: {
        ...location,
        pathname: location.pathname.replace(
          new RegExp(`(${hashtagTypes.join('|')})`),
          tab
        ),
      },
      language: i18n.language,
      shouldFilterSearch: true,
    });

    return `${pathname}${search}`;
  };

  renderHydrateStory = (messageId, index) => {
    const { hashtag, hashtagFeedName, listSelectPath } = this.props;
    const query = `#${hashtag}`;
    return (
      <ObservableHydrateStoryCard
        key={messageId}
        messageId={messageId}
        index={index}
        hashtagFeedName={hashtagFeedName}
        listSelectPath={listSelectPath}
        query={query}
      />
    );
  };
  renderStory = (messageId, index) => {
    const { hashtag, hashtagFeedName, listSelectPath } = this.props;
    const query = `#${hashtag}`;
    return (
      <ObservableStoryCard
        key={messageId}
        messageId={messageId}
        index={index}
        hashtagFeedName={hashtagFeedName}
        listSelectPath={listSelectPath}
        query={query}
      />
    );
  };
  renderStoryLists = () => {
    const { messageIds } = this.props;
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const renderStory = shouldHydrate
      ? this.renderHydrateStory
      : this.renderStory;
    return (
      <MessageCardGrid>
        <ScrollItem
          loader={
            <MessageCardContainer>
              <MessageCardWrapper>
                <MessageCardLoader>
                  <Loading type={'card'} src={LoadingImage} title="loading" />
                </MessageCardLoader>
              </MessageCardWrapper>
            </MessageCardContainer>
          }
        >
          {messageIds.map(renderStory)}
        </ScrollItem>
      </MessageCardGrid>
    );
  };

  renderHydrateFlix = (messageId, index) => {
    const { feedNameWithQuery } = this.props;
    return (
      <ObservableHydrateFlixItem
        key={messageId}
        messageId={messageId}
        index={index}
        feedNameWithQuery={feedNameWithQuery}
      />
    );
  };
  renderFlix = (messageId, index) => {
    const { feedNameWithQuery } = this.props;
    return (
      <ObservableFlixItem
        key={messageId}
        messageId={messageId}
        index={index}
        feedNameWithQuery={feedNameWithQuery}
      />
    );
  };
  renderFlixLists = () => {
    const { messageIds } = this.props;
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const renderFlix = shouldHydrate ? this.renderHydrateFlix : this.renderFlix;
    return (
      <FlixListItemContainer>
        <ScrollItem
          loader={
            <FlixListItemWrapper>
              <FlixListItemLoaderWrapper>
                <FlixListItemLoader>
                  <Loading src={LoadingImage} title="loading" />
                </FlixListItemLoader>
              </FlixListItemLoaderWrapper>
            </FlixListItemWrapper>
          }
        >
          {messageIds.map(renderFlix)}
        </ScrollItem>
      </FlixListItemContainer>
    );
  };
  render() {
    const {
      feedNameRemoteConfigKey,
      hashtagFeedName,
      feedNameWithQuery,
      fetchFeeds,
      hashtag,
      goBack,
      messageCount,
      nextPage,
      isFirstPageFetching,
      isNextPageFetching,
      t,
      type,
      replace,
      messageIds,
    } = this.props;
    const query = `#${hashtag}`;

    return (
      <StyledHashtag>
        <Header>
          <CloseButton
            role="button"
            data-element_id={ButtonId.All.ButtonBack}
            onClick={goBack}
          >
            <CloseIcon src={BackIcon} title="close" />
          </CloseButton>
          <HeaderTitle>
            {query}
            <HeaderDescription>
              {messageCount &&
                t('hashtag_message_count', {
                  ...[messageCount],
                  ns: TranslationNamespace.FEED,
                })}
            </HeaderDescription>
          </HeaderTitle>
          <TabWrapper>
            <Tabs
              onChange={(event, tab) => {
                event?.preventDefault();
                const to = window.location.pathname.replace(
                  new RegExp(`(${hashtagTypes.join('|')})`),
                  tab
                );
                return replace({ ...window.location, pathname: to });
              }}
              value={type}
              style={{ borderBottom: '1px solid rgba(100, 100, 100, 0.25)' }}
            >
              <Tab
                data-element_id={ButtonId.All.TabFlix}
                value={'flix'}
                label={t('title_flix', {
                  ns: TranslationNamespace.GENERAL,
                })}
                href={this.getTabHref('flix')}
              ></Tab>
              <Tab
                data-element_id={ButtonId.All.TabStory}
                value={'story'}
                label={t('broadcast', {
                  ns: TranslationNamespace.GENERAL,
                })}
                href={this.getTabHref('story')}
              ></Tab>
            </Tabs>
          </TabWrapper>
        </Header>
        <Body>
          {!isFirstPageFetching && !messageIds?.length ? null : (
            <ConditionsWrapper>
              <SortButton
                remoteConfigKeyName={feedNameRemoteConfigKey}
                baseFeedName={hashtagFeedName}
                mixpanelButtonId={ButtonId.Hashtag.ButtonHashtagSorting}
                defaultItemI18nKey={'hashtag_default_sorting'}
              />
            </ConditionsWrapper>
          )}
          {isFirstPageFetching && !messageIds?.length ? (
            <LoadingCardWrapper>
              <Loading src={LoadingImage} title="loading" />
            </LoadingCardWrapper>
          ) : (
            <InfiniteScroller
              pageStart={1}
              hasMore={nextPage !== null}
              loadMore={() => {
                nextPage
                  ? fetchFeeds({
                      type: feedNameWithQuery,
                      page: nextPage,
                    })
                  : null;
              }}
              isFetching={isNextPageFetching}
              threshold={100}
            >
              {type === 'story'
                ? this.renderStoryLists()
                : this.renderFlixLists()}
            </InfiniteScroller>
          )}
        </Body>
      </StyledHashtag>
    );
  }
}

Hashtag.propTypes = {
  staticContext: PropTypes.object,
  isAuthed: PropTypes.bool,
  feedNameRemoteConfigKey: PropTypes.string,
  hashtagFeedName: PropTypes.string,
  feedNameWithQuery: PropTypes.string,
  type: PropTypes.string,
  fetchStatus: PropTypes.object,
  isFirstMessageDetailFetched: PropTypes.bool,
  isFirstPageFetching: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  fetchMessageDetail: PropTypes.func,
  fetchFeeds: PropTypes.func,
  replace: PropTypes.func,
  goBack: PropTypes.func,
  fetchHashtagSearch: PropTypes.func,
  hashtag: PropTypes.string,
  messageCount: PropTypes.number,
  nextPage: PropTypes.number,
  listSelectPath: PropTypes.array,
  messageIds: PropTypes.array,
  t: PropTypes.func.isRequired,
  location: PropTypes.object,
};

Hashtag.defaultProps = {
  staticContext: {},
  fetchStatus: {},
  isFirstMessageDetailFetched: false,
  isFirstPageFetching: false,
  isNextPageFetching: false,
  fetchMessageDetail: () => null,
  fetchFeeds: () => null,
  replace: () => null,
  goBack: () => null,
  fetchHashtagSearch: () => null,
  isAuthed: false,
  feedNameRemoteConfigKey: null,
  hashtagFeedName: null,
  feedNameWithQuery: null,
  type: 'flix',
  nextPage: null,
  listSelectPath: [],
  messageIds: [],
  hashtag: '',
  location: {},
};

const ObservableHydrateStoryCard = ({
  messageId,
  index,
  hashtagFeedName,
  listSelectPath,
  query,
}) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  return (
    <MessageCardContainer>
      <HydrationBoundary
        shouldHydrate={inView}
        wrapper={<MessageCardWrapper ref={ref} />}
      >
        <MessageCard
          id={messageId}
          listPath={listSelectPath}
          categoryIndex={index}
          categoryId={hashtagFeedName}
          itemIndexInCategory={index}
          shouldWrapLink
          shouldShowUser
          shouldTrackEvent
          matchedString={query}
          shouldUseLazyThumbnail={false}
        />
      </HydrationBoundary>
    </MessageCardContainer>
  );
};

const ObservableStoryCard = ({
  messageId,
  index,
  hashtagFeedName,
  listSelectPath,
  query,
}) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  return (
    <MessageCardContainer>
      <MessageCardWrapper ref={ref}>
        {inView ? (
          <MessageCard
            id={messageId}
            listPath={listSelectPath}
            categoryIndex={index}
            categoryId={hashtagFeedName}
            itemIndexInCategory={index}
            shouldTrackEvent
            shouldWrapLink
            shouldShowUser
            matchedString={query}
          />
        ) : null}
      </MessageCardWrapper>
    </MessageCardContainer>
  );
};

const ObservableHydrateFlixItem = ({ messageId, index, feedNameWithQuery }) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  return (
    <HydrationBoundary
      shouldHydrate={inView || isServer}
      wrapper={<FlixListItemWrapper ref={ref} />}
    >
      <FlixListItem
        shouldUseLink
        shouldUseLazyImage={false}
        messageId={messageId}
        id={messageId}
        categoryId={feedNameWithQuery}
        itemIndexInCategory={index}
      />
    </HydrationBoundary>
  );
};

const ObservableFlixItem = ({ messageId, index, feedNameWithQuery }) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  return (
    <FlixListItemWrapper ref={ref}>
      <FlixListItem
        shouldUseLink
        messageId={inView ? messageId : null}
        id={messageId}
        categoryId={feedNameWithQuery}
        itemIndexInCategory={index}
      />
    </FlixListItemWrapper>
  );
};

ObservableHydrateStoryCard.propTypes = {
  messageId: PropTypes.string,
  index: PropTypes.number,
  hashtagFeedName: PropTypes.string,
  listSelectPath: PropTypes.string,
  query: PropTypes.string,
};

ObservableStoryCard.propTypes = {
  messageId: PropTypes.string,
  index: PropTypes.number,
  hashtagFeedName: PropTypes.string,
  listSelectPath: PropTypes.string,
  query: PropTypes.string,
};

ObservableHydrateFlixItem.propTypes = {
  messageId: PropTypes.string,
  index: PropTypes.number,
  feedNameWithQuery: PropTypes.string,
};

ObservableFlixItem.propTypes = {
  messageId: PropTypes.string,
  index: PropTypes.number,
  feedNameWithQuery: PropTypes.string,
};

const StyledHashtag = styled.div`
  position: relative;
`;

const Header = styled.header`
  flex: none;
  position: fixed;
  z-index: 3;
  border-bottom: 1px solid ${color.neutral['30_alpha_25']};
  width: 100%;
  height: ${headerHeight}px;
  top: var(--space-to-pinned-notification);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color['grey']['900']};
`;

const HeaderTitle = styled.h1`
  margin: 0px;
  padding: 0px 70px;
  font-size: 16px;
  color: ${textColor.white};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderDescription = styled.p`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  margin-top: 0px;
  margin-bottom: 0px;
  text-align: center;
  font-size: 11px;
  line-height: 1;
  color: ${textColor.black};
`;

const Body = styled.main`
  padding: 16px;
  padding-top: calc(
    ${headerHeight + 45}px + var(--space-to-pinned-notification)
  );
  min-height: 100vh;
  overflow-y: auto;
  ${media.mobile`
    min-height: none;
    padding-bottom: calc(16px + ${footerNavigationHeight}px);
  `};
`;

const ConditionsWrapper = styled.div`
  padding: 20px 12px;
  display: flex;
  justify-content: flex-end;
  > * {
    :first-child {
      margin-left: 0px;
    }
    margin-left: 8px;
  }
  ${media.mobile`
    padding: 12px 16px;
  `};
  :empty {
    padding: 0px;
    margin-bottom: 20px;
    ${media.mobile`
      margin-bottom: 12px;
    `};
  }
`;

const MessageCardGrid = styled.div`
  display: grid;
  gap: 12px 8px;
  grid-template-columns: repeat(auto-fill, minmax(109px, 1fr));
`;

const MessageCardContainer = styled.div`
  position: relative;
  padding-top: 142%;
`;

const MessageCardWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const MessageCardLoader = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.neutral[30]};
  background-image: linear-gradient(
    to bottom,
    ${color.neutral['0_alpha_30']},
    ${color.neutral['0_alpha_5']} 17%,
    ${color.neutral['0_alpha_30']} 53%,
    ${color.neutral['0_alpha_50']}
  );
  border-radius: 4px;
`;

const CloseButton = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }
`;

const CloseIcon = styled.img`
  height: 100%;
  width: auto;
  vertical-align: top;
`;

const LoadingCardWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  margin-top: 52px;
  justify-content: center;
  > img {
    width: 32px;
    height: 32px;
  }
`;

const Loading = styled.img`
  height: auto;
  ${({ type }) => (type === 'card' ? 'width: 50%;' : 'width: 100%;')}
  animation: 1.4s infinite ${Spinning};
`;

const TabWrapper = styled.div`
  position: fixed;
  width: 100%;
  top: calc(64px + var(--space-to-pinned-notification));
  background-color: ${color['grey']['900']};
  z-index: 3;
`;

const FlixListItemContainer = styled.div``;
const FlixListItemWrapper = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
  margin-bottom: 8px;
  :first-child {
    margin-top: 8px;
  }
`;

const FlixListItemLoader = styled.div`
  width: 48px;
  height: 48px;
`;

const FlixListItemLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 92px;
`;
export default withTranslation()(withRouter(Hashtag));
